import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState, StatCard } from "../components/shared";
import { formatNum, can, WASTE_CATEGORIES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Waste() {
  const { user } = useAuth();
  const editable = can(user.role, "waste");
  const empty = { category: "Waste", quantity: 0, unit: "kg", date: new Date().toISOString().slice(0, 10), source: "", note: "" };
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [l, s] = await Promise.all([api.get("/waste"), api.get("/waste/summary")]);
    setItems(l.data); setSummary(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!(form.quantity > 0)) { toast.error("Enter a quantity"); return; }
    try { await api.post("/waste", form); toast.success("Waste entry recorded"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete waste entry?")) return; await api.delete(`/waste/${id}`); load(); };

  return (
    <div data-testid="waste-page">
      <PageHeader title="Waste Log" subtitle="Track production waste & scrap by category"
        action={editable && <Button className="rounded-none gap-2" data-testid="create-waste-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus className="w-4 h-4" /> New Waste Entry</Button>} />

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {WASTE_CATEGORIES.map((c, i) => (
            <StatCard key={c} label={c} value={formatNum(summary.by_category[c] || 0)} index={i} />
          ))}
          <StatCard label="Total" value={formatNum(summary.total)} accent index={5} />
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No waste logged yet" hint="Record waste, H&C, lumps, grinded and sweep quantities generated during production." />
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Source</TableHead>
              <TableHead className="text-right">Quantity</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((w) => (
                <TableRow key={w.id} data-testid="waste-row">
                  <TableCell className="font-mono text-sm">{w.date}</TableCell>
                  <TableCell><span className="inline-block px-2 py-0.5 text-xs font-mono font-semibold uppercase bg-secondary">{w.category}</span></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{w.source || "—"}</TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatNum(w.quantity)} {w.unit}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{w.note}</TableCell>
                  <TableCell className="text-right">{editable && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-destructive" onClick={() => del(w.id)} data-testid="delete-waste-btn"><Trash2 className="w-4 h-4" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">Record Waste / Scrap</DialogTitle><DialogDescription>Standalone waste log entry — does not deduct raw material stock.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="waste-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WASTE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" className="rounded-none mt-1" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantity</Label><Input type="number" step="any" className="rounded-none mt-1" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: parseFloat(e.target.value || 0) }))} data-testid="waste-qty-input" /></div>
              <div><Label>Unit</Label><Input className="rounded-none mt-1" value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} data-testid="waste-unit-input" /></div>
            </div>
            <div><Label>Source (optional)</Label><Input className="rounded-none mt-1" value={form.source} placeholder="e.g. product / batch / line" onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} data-testid="waste-source-input" /></div>
            <div><Label>Note</Label><Input className="rounded-none mt-1" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-waste-btn">Save Entry</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
